/**
 * สูตรหนึ่งชุดของสินค้าหนึ่งตัวในโรงงานหนึ่งแห่ง — ฉบับที่ใช้อยู่ + ฉบับก่อนหน้า +
 * การเขียนฉบับถัดไป
 *
 * BOM กับ Routing ใช้ component ตัวนี้ร่วมกันผ่าน `FormulaSpec` เพราะ contract เขียน
 * ไว้ว่า "กติกาเดียวกับ BOM ทุกข้อ" — ต่างกันแค่ชื่อคอลัมน์ของแถว
 *
 * สองก้อนที่หน้านี้ดึงมาและ **ต้องไม่ถูกเดาแทนกัน**:
 *
 * 1. `GET .../{bom|routing}` — ฉบับที่ใช้อยู่จริง (`status = ACTIVE` และ
 *    `valid_to IS NULL`) ซึ่งคือสูตรที่โรงงานกำลังผลิตตาม
 * 2. `GET .../{bom|routing}/revisions` — ทุกฉบับรวมที่ปิดไปแล้ว ซึ่งใบสั่งผลิตเก่า
 *    ยังอ้างถึงอยู่และต้องอ่านย้อนหลังได้ตลอดอายุของ order
 *
 * "ยังไม่ได้เขียนสูตร" (`200` + body ที่บอกว่าไม่มี) ถูกแสดงเป็นสถานะว่างที่ชวนให้
 * เขียนฉบับแรก **ไม่ใช่ error** และไม่ใช่ 404 ซึ่งแปลว่าไม่มีสินค้าตัวนี้
 *
 * ไม่มีสูตรก้อนไหนถูกเขียนลง storage ใด ๆ (D-049 อนุญาตเฉพาะ session token) —
 * สำเนาสูตรที่ค้างในเบราว์เซอร์คือภาพเก่าของสิ่งที่วิศวกรอาจเพิ่งเขียนฉบับใหม่ทับ
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { BomFailureNotice } from './BomFailureNotice';
import { FormulaCreateForm } from './FormulaCreateForm';
import { isInForce, revisionStatusLabel, revisionStatusTone, validityLabel } from './bomView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { orDash } from '../products/productView';
import {
  fetchCurrentFormula,
  fetchFormulaRevisions,
  type FormulaFailure,
} from '../../lib/bom/client';
import type { CurrentFormula, FormulaRevision, FormulaSpec } from '../../lib/bom/types';

export interface FormulaPanelProps<TRow> {
  readonly spec: FormulaSpec<TRow>;
  readonly productId: string;
  readonly plantCode: string;
  /** `can_create` ของ `BOM_ROUTING` ที่เซิร์ฟเวอร์ส่งมา — UX เท่านั้น ไม่ใช่ด่าน */
  readonly canCreate: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type CurrentState<TRow> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly current: CurrentFormula<TRow> }
  | FormulaFailure;

type HistoryState<TRow> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly revisions: readonly FormulaRevision<TRow>[] }
  | FormulaFailure;

function RowTable<TRow>({
  spec,
  rows,
  testIdPrefix,
  caption,
}: {
  readonly spec: FormulaSpec<TRow>;
  readonly rows: readonly TRow[];
  readonly testIdPrefix: string;
  readonly caption: string;
}) {
  if (rows.length === 0) {
    return (
      <p className="cap-card__foot" data-testid={`${testIdPrefix}-rows-empty`}>
        ฉบับนี้ไม่มี{spec.rowNoun}ที่อ่านได้จากคำตอบของเซิร์ฟเวอร์
      </p>
    );
  }

  return (
    <div className="cap-table-wrap">
      <table className="cap-table">
        <caption className="cap-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {spec.fields.map((field) => (
              <th scope="col" key={field.key}>
                {field.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={spec.rowKey(row)} data-testid={`${testIdPrefix}-row-${spec.rowKey(row)}`}>
              {spec.fields.map((field, index) =>
                index === 0 ? (
                  <th scope="row" className="cap-table__row-head" key={field.key}>
                    {spec.cell(row, field.key)}
                  </th>
                ) : (
                  <td
                    key={field.key}
                    className={
                      field.kind === 'decimal' || field.kind === 'integer'
                        ? 'cap-table__code'
                        : field.kind === 'optional-code'
                          ? 'cap-table__muted'
                          : 'cap-table__code'
                    }
                    data-testid={`${testIdPrefix}-cell-${spec.rowKey(row)}-${field.key}`}
                  >
                    {/* ⚠️ ค่า NUMERIC แสดงเป็น string ที่ได้รับมาตรง ๆ ไม่ผ่าน Number */}
                    {orDash(spec.cell(row, field.key))}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FormulaPanel<TRow>({
  spec,
  productId,
  plantCode,
  canCreate,
  apiBaseUrl,
  fetchImpl,
}: FormulaPanelProps<TRow>) {
  const [current, setCurrent] = useState<CurrentState<TRow>>({ status: 'loading' });
  const [history, setHistory] = useState<HistoryState<TRow>>({ status: 'loading' });
  const [created, setCreated] = useState<FormulaRevision<TRow> | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setCurrent({ status: 'loading' });
    setHistory({ status: 'loading' });

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchCurrentFormula(spec, productId, plantCode, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setCurrent(result);
    });

    void fetchFormulaRevisions(spec, productId, plantCode, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setHistory(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [spec, productId, plantCode, apiBaseUrl, fetchImpl, reloadToken]);

  const refresh = useCallback(() => {
    if (mounted.current) {
      setReloadToken((token) => token + 1);
    }
  }, []);

  const inForce =
    current.status === 'ready' && current.current.present ? current.current.revision : null;

  return (
    <section className="cap-card" aria-label={spec.title} data-testid={`formula-${spec.kind}`}>
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          {spec.title}
          <InfoHint
            label={`เกี่ยวกับขอบเขตโรงงานของ${spec.title}`}
            text={
              <>
                แสดงเฉพาะโรงงาน <code className="cap-table__code">{plantCode}</code> — สินค้าตัวเดียวกัน
                มีสูตรคนละฉบับในอีกโรงงานได้ และนั่นถูกต้องตามการออกแบบ (D-004)
              </>
            }
          />
        </h2>
      </div>

      {current.status === 'loading' ? (
        <p className="cap-card__foot" data-testid="formula-current-loading">
          กำลังโหลด{spec.title}ที่ใช้อยู่…
        </p>
      ) : null}

      {current.status !== 'loading' && current.status !== 'ready' ? (
        <BomFailureNotice
          failure={current}
          context="read"
          plantCode={plantCode}
          onRetry={refresh}
        />
      ) : null}

      {current.status === 'ready' && !current.current.present ? (
        <div className="cap-notice" data-testid="formula-empty" role="status">
          <StatusBadge tone="planned">ยังไม่ได้เขียนฉบับแรก</StatusBadge>
          <h3 className="cap-notice__title">สินค้านี้ยังไม่มี{spec.title}ในโรงงานนี้</h3>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบว่า “ยังไม่มีฉบับที่ใช้อยู่” ซึ่งเป็นคำตอบปกติ ไม่ใช่ข้อผิดพลาด
            และคนละเรื่องกับ “ไม่พบสินค้านี้” — เขียนฉบับแรกได้จากฟอร์มด้านล่าง
            ฉบับแรกก็ยังเป็นชุดเต็ม และเลข revision เซิร์ฟเวอร์เป็นผู้กำหนดให้เอง
          </p>
        </div>
      ) : null}

      {inForce === null ? null : (
        <>
          <div className="cap-meta cap-meta--flush">
            <div className="cap-meta__item">
              <div className="cap-meta__label">ฉบับที่ใช้อยู่</div>
              <div className="cap-meta__value cap-table__code" data-testid="formula-in-force-revision">
                revision {inForce.revision}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สถานะ</div>
              <div className="cap-meta__value">
                <StatusBadge tone={revisionStatusTone(inForce.status)}>
                  {revisionStatusLabel(inForce.status)}
                </StatusBadge>
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">มีผลตั้งแต่</div>
              <div className="cap-meta__value" data-testid="formula-in-force-valid-from">
                {inForce.valid_from}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ถูกปิดเมื่อ</div>
              <div className="cap-meta__value" data-testid="formula-in-force-valid-to">
                {orDash(inForce.valid_to)}
              </div>
            </div>
          </div>

          <RowTable
            spec={spec}
            rows={inForce.rows}
            testIdPrefix="formula-current"
            caption={`${spec.rowNoun}ทั้งชุดของฉบับที่ใช้อยู่ (revision ${inForce.revision})`}
          />
        </>
      )}

      {current.status !== 'ready' ? null : (
        <p className="cap-card__foot" data-testid="formula-no-edit-note">
          <strong>ที่นี่ไม่มีปุ่มแก้ไขและไม่มีปุ่มลบ — โดยเจตนา</strong>{' '}
          การเปลี่ยนสูตรคือการเขียนฉบับถัดไป ฉบับเดิมถูกปิดด้วยวันที่มีผลของฉบับใหม่
          แล้วยังอ่านย้อนหลังได้ตลอด เพราะใบสั่งผลิตที่สั่งปล่อยไปแล้วผูกกับฉบับที่มันถูก
          สร้างมาจาก และต้องอ่านสูตรนั้นได้จนกว่าจะปิดงาน
        </p>
      )}

      {created === null ? null : (
        <div className="cap-banner" role="status" data-testid="formula-created">
          <strong>บันทึกฉบับใหม่แล้ว</strong>
          <span>
            เซิร์ฟเวอร์กำหนดให้เป็น{' '}
            <code className="cap-table__code" data-testid="formula-created-revision">
              revision {created.revision}
            </code>{' '}
            มีผลตั้งแต่ {created.valid_from} — เลขนี้มาจากเซิร์ฟเวอร์ ไม่ใช่ค่าที่หน้าจอเดาไว้ล่วงหน้า
          </span>
        </div>
      )}

      {current.status === 'loading' ? null : canCreate ? (
        <FormulaCreateForm
          spec={spec}
          productId={productId}
          plantCode={plantCode}
          currentRows={inForce === null ? null : inForce.rows}
          apiBaseUrl={apiBaseUrl}
          fetchImpl={fetchImpl}
          onCreated={(revision) => {
            setCreated(revision);
            // แสดงสิ่งที่เซิร์ฟเวอร์เก็บไว้จริง ไม่ใช่สิ่งที่หน้าจอเดาว่าจะเป็น
            refresh();
          }}
        />
      ) : (
        <div className="cap-notice" data-testid="formula-create-unavailable" role="status">
          <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
          <h3 className="cap-notice__title">สิทธิ์ชุดนี้ไม่มีการเขียนฉบับใหม่</h3>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
            ของโมดูล <code className="cap-table__code">BOM_ROUTING</code> หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก
            แต่ยังดูสูตรที่มีอยู่และฉบับก่อนหน้าได้
          </p>
          <p className="cap-notice__note">
            การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก —
            ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
          </p>
        </div>
      )}

      <div className="cap-card__head">
        <h3 className="cap-card__title">
          ฉบับก่อนหน้า
          <InfoHint
            label="เกี่ยวกับฉบับก่อนหน้า"
            text="ฉบับที่ถูกแทนที่ไปแล้วยังอ่านได้เสมอพร้อมช่วงเวลาที่เคยมีผล — ใบสั่งผลิตที่สั่งปล่อยไปก่อนหน้านี้ผูกอยู่กับฉบับเหล่านี้"
          />
        </h3>
      </div>

      {history.status === 'loading' ? (
        <p className="cap-card__foot" data-testid="formula-history-loading">
          กำลังโหลดฉบับก่อนหน้า…
        </p>
      ) : null}

      {history.status !== 'loading' && history.status !== 'ready' ? (
        <BomFailureNotice
          failure={history}
          context="read"
          plantCode={plantCode}
          onRetry={refresh}
        />
      ) : null}

      {history.status === 'ready' && history.revisions.length === 0 ? (
        <p className="cap-card__foot" data-testid="formula-history-empty">
          ยังไม่มีฉบับใดถูกบันทึกไว้สำหรับสินค้านี้ในโรงงานนี้
        </p>
      ) : null}

      {history.status !== 'ready'
        ? null
        : history.revisions.map((revision) => (
            <section
              className="cap-card cap-card--table"
              key={`${revision.revision}-${revision.valid_from}`}
              data-testid={`formula-revision-${revision.revision}`}
              data-in-force={isInForce(revision) ? 'true' : 'false'}
              aria-label={`${spec.title} revision ${revision.revision}`}
            >
              <div className="cap-card__head">
                <h4 className="cap-card__title">revision {revision.revision}</h4>
                <span className="cap-card__hint">
                  <StatusBadge tone={revisionStatusTone(revision.status)}>
                    {revisionStatusLabel(revision.status)}
                  </StatusBadge>{' '}
                  <span data-testid={`formula-revision-${revision.revision}-validity`}>
                    {validityLabel(revision)}
                  </span>
                  {isInForce(revision) ? ' — ฉบับนี้คือฉบับที่ใช้อยู่' : ''}
                </span>
              </div>
              <RowTable
                spec={spec}
                rows={revision.rows}
                testIdPrefix={`formula-revision-${revision.revision}`}
                caption={`${spec.rowNoun}ของ revision ${revision.revision}`}
              />
            </section>
          ))}
    </section>
  );
}
